/**
 * THE HOURS, DRAWN AS THE RULE THAT WAS ALREADY THERE.
 *
 * -- WHAT THIS IS FOR ----------------------------------------------------------
 *
 * CONTRACT 1.5l(a) makes per-species mastery the one system the game has, and
 * `src/sim/mastery.ts` made the cost of the player's one verb a real number:
 * ONE POINT IS ONE TICK OF A CAST THAT LANDED THAT SPECIES, so mastery is read in
 * hours by dividing by 3,600 and there is no conversion constant to get wrong.
 * Nothing on any surface read that field. The ledger showed a name and a landed
 * count and said nothing at all about where a player's hours had gone, which is
 * the whole content of the choice they are being asked to make.
 *
 * -- WHY IT IS NOT A BAR, AND THE BAR WAS THE DEFAULT ANSWER --------------------
 *
 * The measured `idle-game-ui` register in the design library is the genre's own
 * convention for a progressing quantity, and both captures show it: a caption, a
 * fraction, and a horizontal fill inside a track (Trimps: "Food / 0 500 / [fill]").
 * That is the template answer to this brief and it fails here three times over.
 * A track with a fill is a container, and the sheet has no containers - the design
 * ledger's standing verdict on this project was a flat panel "with rounded corners
 * and a thin border... just a plain colored box". A fill needs a colour that is not
 * ink and not paper, and the sheet runs at ZERO accent hues against a corpus median
 * of 3. And a track drawn beside handwriting is two art languages on one surface,
 * which is the ledger's own universal rule about shipping two fidelity tiers.
 *
 * So the measure is a mark the sheet ALREADY makes. Every ledger row is ruled
 * underneath - `src/render/chart.ts` paintLedger, one hairline in the paper's own
 * shade, described there as "structure, not decoration". That rule carries no
 * information today beyond which row is in force. Here it carries the hours:
 *
 *   INK    from the left edge of the rule, for the fraction of MASTERY_FULL spent.
 *   SHADE  the hairline the sheet already draws, for the rest.
 *
 * No new pixel of height, no new colour, no new shape, and nothing to explain. A
 * fish you have never chased keeps exactly the rule it has today; a fish you have
 * mastered is ruled solidly under its name and its count, which is what a closed
 * column looks like in a book. Across twenty-four rows it reads at a glance as a
 * weight distribution down the page, before a single figure has been read - which
 * is the accepted baseline's "measures and charts drawn by the structure itself".
 *
 * -- THE THIRD STATE, AND THE DEFECT IT IS AVOIDING ----------------------------
 *
 * The surfaces lane has an open finding that the inactive leaf heading wears the
 * same wash as a locked row, so "this tab is not selected" and "this row is locked
 * to you" are told apart by nothing. A third state colliding with either would be
 * the same defect again, so this file introduces NO NEW TONE. It spends only the
 * two the ledger already rules with.
 *
 * That leaves one real collision to resolve, and it is resolved by SHAPE. Today
 * the row in force is struck twice: a solid blot in the gutter, and its whole rule
 * drawn in ink. If the rule means hours, the ink under the chosen fish would mean
 * two things at once. So on the row in force the UNSPENT part of the rule is drawn
 * DASHED in ink rather than solid in shade: the row is still struck all the way
 * across, still struck twice, and solid-versus-dashed is a difference in shape that
 * survives the hue being thrown away, which CONTRACT section 6 requires of every
 * state distinguished by colour. Spent hours are solid on every row; only the
 * remainder changes pattern, and only on the one row the blot is already on.
 *
 * -- WHAT IT COSTS TO DRAW ------------------------------------------------------
 *
 * The host repaints the chart only when its key moves. `hoursKey` is the key this
 * file contributes, and it is the QUANTISED PIXEL LENGTH of each rule, never the
 * raw point total: the rule is 376 art px against MASTERY_FULL = 72,000 points, so
 * one pixel is 191 points and a repaint is possible at most once per 191 seconds of
 * landing casts per row. When the ledger is shut the key is empty and mastery
 * cannot wake the host at all.
 */

import { CAST_PAD, INK, SHADE, type ChartCopy, type ChartLayout } from '../render/chart';
import type { Bitmap } from '../render/glyphs';
import { put } from '../render/glyphs';
import { MASTERY_FULL } from '../sim/index';

/**
 * The mastery points behind each ledger row, aligned to `ChartCopy.ledger`.
 *
 * `null` means THIS ROW HAS NO SUCH DATUM and its rule is left exactly as the chart
 * painted it. Three rows are null and each for its own reason: a species still
 * locked (you cannot land it, so no hour can have been spent on it), every row of
 * the places leaf (a landfall has no mastery), and any row the caller could not
 * resolve. Drawing a zero-length measure and drawing nothing are the same pixels;
 * the distinction is kept because a future leaf with a real zero must not be
 * silently indistinguishable from a leaf with no measure at all.
 */
export type HoursRows = readonly (number | null)[];

/** The fraction of a full mastery this many points is, clamped to 0..1. */
export function masteryFraction(points: number): number {
  if (!Number.isFinite(points) || points <= 0) return 0;
  return Math.min(1, points / MASTERY_FULL);
}

/**
 * Art pixels of ink in a rule `ruleW` wide at `points` of mastery.
 *
 * FLOOR, NOT ROUND, at both ends, and the reason is the one the run keeps meeting:
 * a measure that rounds up shows ink for an hour nobody has spent. One point of
 * mastery must not draw a pixel, and one pixel short of the end must not draw a
 * full rule. `ruleW` in and `ruleW` out happens only at MASTERY_FULL or beyond.
 */
export function hoursInk(points: number, ruleW: number): number {
  if (ruleW <= 0) return 0;
  const f = masteryFraction(points);
  if (f >= 1) return ruleW;
  return Math.max(0, Math.min(ruleW, Math.floor(f * ruleW)));
}

/**
 * WHERE A ROW'S RULE IS, READ OFF THE CHART'S OWN LAYOUT.
 *
 * `chartLayout` publishes each visible row's pen x and its rect, and the chart
 * paints that row's rule from `x - 2` to `rect.x1`. Both numbers come from the
 * layout, so this file owns no copy of the ledger's geometry: the day a column
 * moves, the measure moves with it. That is the same "one layout, two readers"
 * rule the chart states about its own painter and its own hit testing.
 */
export interface RuleSpan {
  readonly index: number;
  /** First art px of the rule, in bitmap coordinates. */
  readonly x0: number;
  /** One past the last art px of the rule. */
  readonly x1: number;
  /** The rule's own row, in bitmap coordinates. */
  readonly y: number;
}

export function ruleSpans(layout: ChartLayout): readonly RuleSpan[] {
  return layout.rows.map((r) => ({
    index: r.index,
    x0: r.x - 2,
    x1: r.rect.x1,
    y: CAST_PAD + r.base + 3,
  }));
}

/**
 * Everything about the measure that could change a pixel, as one string, for the
 * host's repaint key. Empty while the ledger is shut, so a closed sheet is never
 * woken by a mastery tick.
 */
export function hoursKey(copy: ChartCopy, layout: ChartLayout, hours: HoursRows): string {
  if (copy.lift <= 0) return '';
  const parts: string[] = [];
  for (const span of ruleSpans(layout)) {
    const points = hours[span.index];
    if (points === null || points === undefined) continue;
    const marked = copy.ledger[span.index]?.marked === true;
    parts.push(`${span.index}:${hoursInk(points, span.x1 - span.x0)}${marked ? 'm' : ''}`);
  }
  return parts.join(',');
}

/**
 * Rule the hours under every row that has them. Pure in its inputs; it writes only
 * the one row of pixels the chart had already ruled, and never outside a span the
 * chart's own layout published.
 */
export function paintHours(b: Bitmap, copy: ChartCopy, layout: ChartLayout, hours: HoursRows): void {
  if (copy.lift <= 0) return;
  for (const span of ruleSpans(layout)) {
    const points = hours[span.index];
    if (points === null || points === undefined) continue;
    const width = span.x1 - span.x0;
    if (width <= 0) continue;
    const spent = hoursInk(points, width);
    const marked = copy.ledger[span.index]?.marked === true;
    for (let rx = span.x0; rx < span.x1; rx++) {
      const inked = rx - span.x0 < spent;
      // The remainder: the paper's hairline on an ordinary row, and ink laid every
      // other pixel on the row in force, so that row is still struck all the way
      // across and still told from the rest by a shape rather than by a tone.
      const ink = inked ? INK : marked && (rx & 1) === 0 ? INK : SHADE;
      put(b, rx, span.y, ink);
    }
  }
}
